import PropTypes from "prop-types";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  buildPrice,
  getInvoicesRowAmount,
  getInvoicesTotalValue,
} from "components/helpers";
import {
  SPECIAL_LINE_TYPES,
  isSpecialLine,
} from "routes/revenue_opportunity/form/invoiceLines";
import { useInvoiceProducts } from "routes/revenue_opportunity/form/sections/invoices/useInvoiceProducts";

const PRODUCT_UNAVAILABLE = "Unavailable";

const specialLineLabel = invoice =>
  SPECIAL_LINE_TYPES.find(type => invoice[type.flag])?.label;

/** The read-only invoice lines, comments, and total of a Revenue Opportunity. */
const RevenueOpportunityInvoicesTab = ({ revenueOpportunity }) => {
  const invoices = revenueOpportunity.invoices ?? [];
  const { data: products, isPending, isError } = useInvoiceProducts(invoices);

  if (isPending) {
    return (
      <div className="tw:flex tw:justify-center tw:p-6">
        <Spinner />
      </div>
    );
  }

  // A failed product lookup only costs the product names; the lines, amounts,
  // and total come from the record and still render.
  const productNames = new Map(
    [
      ...(products?.productOptions ?? []),
      ...(products?.disabledProductOptions ?? []),
    ].map(option => [option.value, option.text])
  );
  const productLabel = invoice =>
    isSpecialLine(invoice)
      ? specialLineLabel(invoice)
      : (productNames.get(invoice.product_id) ?? PRODUCT_UNAVAILABLE);

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      {isError && (
        <Alert>
          <AlertDescription>
            Could not load the product names. The invoice lines and totals are
            shown without them.
          </AlertDescription>
        </Alert>
      )}
      {invoices.length === 0 ? (
        <p className="tw:text-sm tw:text-muted-foreground">No invoice lines.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead className="tw:text-right">Amount</TableHead>
              <TableHead className="tw:text-right">Term</TableHead>
              <TableHead className="tw:text-right">Qty</TableHead>
              <TableHead>License Start</TableHead>
              <TableHead>License End</TableHead>
              <TableHead className="tw:text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map(invoice => (
              <TableRow key={invoice.id}>
                <TableCell
                  className={isSpecialLine(invoice) ? "tw:italic" : undefined}
                >
                  {productLabel(invoice)}
                </TableCell>
                <TableCell className="tw:text-right">
                  {buildPrice(parseFloat(invoice.amount) || 0)}
                </TableCell>
                <TableCell className="tw:text-right">{invoice.term}</TableCell>
                <TableCell className="tw:text-right">
                  {invoice.quantity}
                </TableCell>
                <TableCell>{invoice.license_start_date}</TableCell>
                <TableCell>{invoice.license_end_date}</TableCell>
                <TableCell className="tw:text-right tw:font-medium">
                  {buildPrice(getInvoicesRowAmount(invoice))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:md:grid-cols-[1fr_auto]">
        <div className="tw:flex tw:flex-col tw:gap-1">
          <span className="tw:text-sm tw:font-medium tw:text-foreground">
            Comments
          </span>
          <p className="tw:whitespace-pre-wrap tw:text-sm tw:text-(--color-text-secondary)">
            {revenueOpportunity.comments || "None"}
          </p>
        </div>
        <div className="tw:flex tw:flex-col tw:items-end tw:gap-1 tw:px-4">
          <span className="tw:text-sm tw:text-muted-foreground">
            Total Value
          </span>
          <span className="tw:text-2xl tw:font-semibold tw:text-foreground">
            {buildPrice(getInvoicesTotalValue(invoices))}
          </span>
        </div>
      </div>
    </div>
  );
};

RevenueOpportunityInvoicesTab.propTypes = {
  revenueOpportunity: PropTypes.shape({
    invoices: PropTypes.array,
    comments: PropTypes.string,
  }).isRequired,
};

export default RevenueOpportunityInvoicesTab;
